import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { BuildingModelAssetBundle } from '../models/building-model-assets.model';
import { BuildingModelAssetsService } from './building-model-assets.service';
import { ApiService } from './api.service';
import { ConfigurationManagerService } from './configuration-manager.service';

/**
 * Same transport and interceptors as other services (`ApiService` → `HttpClient`).
 * Mock JSON vs live API: `building3dAssetsLoadOptions` in this file only.
 */
@Injectable()
export class HttpBuildingModelAssetsService extends BuildingModelAssetsService {
  private cache = new Map<string, Observable<BuildingModelAssetBundle>>();

  constructor(private readonly api: ApiService) {
    super();
  }

  override getAssetBundle(resolutionKey?: string | null): Observable<BuildingModelAssetBundle> {
    const key =
      resolutionKey != null && String(resolutionKey).trim() !== ''
        ? String(resolutionKey).trim() // This will be the perimeterId
        : '';
    if (!key) {
      return throwError(() => new Error('Resolution key (perimeterId) is required for building 3D assets'));
    }

    if (this.cache.has(key)) {
      return this.cache.get(key)!;
    }

    const url = `${ConfigurationManagerService.settings?.BaseURL}${ConfigurationManagerService.settings.PerimeterGlb}`;
    const body = { perimeterId: key };
    const request$ = this.api.post<unknown>(url, body).pipe(
      map((response) => this.normalizeBundle(response)),
      catchError(() => of({ modelId: '', glbUrl: '' })),
      shareReplay(1)
    );

    this.cache.set(key, request$);
    return request$;
  }

  private normalizeBundle(body: unknown): BuildingModelAssetBundle {
    if (!body || typeof body !== 'object') {
      return { modelId: '', glbUrl: '' };
    }
    const o = body as Record<string, any>;
    const modelId = o['perimeterId'] || ''; 
    let glbUrl = o['glbPath'] || '';

    if (glbUrl === 'N/A') {
      glbUrl = '';
    }

    return { 
      modelId, 
      glbUrl,
      floors: o['floors'] || []
    };
  }
}
